import { PageSection, TextContent, Text, PageSectionVariants, Alert, Divider, EmptyState, EmptyStateBody, EmptyStateHeader } from '@patternfly/react-core';
import { useSelector } from 'react-redux';

import './license-page.css';
import { useTranslation } from 'react-i18next';
import { StoreState } from '../../store/store';
import { appConfigSelectors } from '../../store/appConfig/appConfigSlice';
import { configurationTargetSelectors } from '../../store/configurationTarget/configurationTargetSlice';
import TabbedSupportedModelTypes, { ModelTypeContent } from '../../components/TabbedSupportedModelTypes/TabbedSupportedModelTypes';
import LicenseView from './LicenseView';
import UpdateLicense from './UpdateLicense';
import { ModelType } from '../../store/global-types/customization-types';

const LicensePage = () => {
    const appConfig = useSelector(appConfigSelectors.selectAppConfig);
    const currentTarget = useSelector(configurationTargetSelectors.selectCurrent);

    // reload license status whenever the page is reloaded, re-opened, or client is changed
    const { t } = useTranslation();

    const licenseStatus = useSelector((state: StoreState) => state.license.licenseStatus);
    const licenseStatusError = useSelector((state: StoreState) => state.license.licenseStatusError);

    const allModelTypes = Object.values(ModelType).filter(modelType => modelType !== ModelType.None);

    const modelTypeContent: ModelTypeContent[] = licenseStatus && allModelTypes.map(modelType => ({
        modelType,
        content: (
            <LicenseView
                licenseStatus={licenseStatus.filter(license =>
                    license.modelTypes.includes(modelType)
                )}
                modelType={modelType}
            />
        )
    })) || [];

    // Show the license update field if allowed in config AND a configuration target is selected (there
    // should always be one selected even in local mode)
    const isLicenseUpdateFieldShown = appConfig && appConfig.allowLicenseUpdate && currentTarget;

    return (
        <>
            <PageSection variant={PageSectionVariants.default}>
                <TextContent>
                    <Text component="h1">{t('page.license.title')}</Text>
                </TextContent>
            </PageSection>

            <PageSection variant={PageSectionVariants.light} data-testid="license-page">
                {appConfig && appConfig.showErrorsInUi && licenseStatusError !== null && (
                    <PageSection className="selectable">
                        <Alert variant="danger" isInline title="An error occurred when trying to retrieve license information">
                            <p>An error occurred when trying to retrieve license information:</p>
                            <p>{licenseStatusError}</p>
                        </Alert>
                    </PageSection>
                )}

                {!currentTarget && (
                    <EmptyState>
                        <EmptyStateHeader titleText={<>{t('licensePage.noConfigurationTarget.title')}</>} headingLevel="h4" />
                        <EmptyStateBody>{t('licensePage.noConfigurationTarget.body')}</EmptyStateBody>
                    </EmptyState>
                )}

                {currentTarget && licenseStatusError === null && licenseStatus !== null && licenseStatus.length === 0 && (
                    <EmptyState>
                        <EmptyStateHeader titleText={<>{t('licensePage.noLicense.title')}</>} headingLevel="h4" />
                        <EmptyStateBody>{t('licensePage.noLicense.body')}</EmptyStateBody>
                    </EmptyState>
                )}

                {currentTarget && licenseStatusError === null && licenseStatus !== null && licenseStatus.length > 0 && (
                    <PageSection variant={PageSectionVariants.light} className="limit-width">
                        <TabbedSupportedModelTypes
                            content={modelTypeContent}
                            enableChangeCurrentModelType
                        />
                    </PageSection>
                )}

                {isLicenseUpdateFieldShown && (
                    <>
                        <Divider className="content-divider" />
                        <UpdateLicense />
                    </>
                )}
            </PageSection>
        </>
    );
}

export default LicensePage;
